import React, { useEffect, useState } from 'react';
import {
  CloudArrowUp, ArrowClockwise, Database, Trash, Plug, DownloadSimple,
  UploadSimple, Warning, CheckCircle, FloppyDisk, Eye, EyeSlash
} from '@phosphor-icons/react';

const fmtSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
};

export default function BackupRestoreSection({ api }) {
  const [cfg, setCfg] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [msg, setMsg] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null); // {name}
  const [confirmDelete, setConfirmDelete] = useState(null);   // {name}
  const [uploadFile, setUploadFile] = useState(null);
  const [confirmUploadRestore, setConfirmUploadRestore] = useState(false);
  const [uploading, setUploading] = useState(false);

  const flash = (type, m) => {
    setMsg({ type, m });
    setTimeout(() => setMsg(null), 4500);
  };

  const fetchAll = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get('/backups/config'),
        api.get('/backups/snapshots').catch(() => ({ data: { snapshots: [] } })),
      ]);
      setCfg(c.data);
      setSnapshots(s.data.snapshots || []);
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Failed to load backup settings');
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [api]);

  const updateCfg = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!cfg.enabled,
        access_key_id: cfg.access_key_id || '',
        endpoint_url: cfg.endpoint_url || '',
        bucket: cfg.bucket || '',
        region: cfg.region || 'auto',
        schedule_hour_ist: Number(cfg.schedule_hour_ist) || 3,
        daily_retention: Number(cfg.daily_retention) || 30,
        monthly_retention: Number(cfg.monthly_retention) || 12,
      };
      // Only send secret if user typed a new one (non-empty)
      if (cfg.secret_access_key && cfg.secret_access_key.trim()) {
        payload.secret_access_key = cfg.secret_access_key.trim();
      }
      const r = await api.put('/backups/config', payload);
      setCfg(r.data);
      flash('success', 'Backup settings saved');
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/backups/test-connection');
      if (r.data.ok) flash('success', `Connection OK — bucket "${r.data.bucket}" reachable`);
      else flash('error', `Connection failed: ${r.data.error}`);
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Test failed');
    }
    setTesting(false);
  };

  const handleRunNow = async () => {
    if (!cfg.enabled) { flash('error', 'Enable backups first'); return; }
    setRunning(true);
    try {
      await api.post('/backups/run');
      flash('success', 'Backup started in background. Check Status below in ~1 min.');
      // Poll snapshots every 5s for 60s
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        await fetchAll();
        if (attempts >= 12) clearInterval(poll);
      }, 5000);
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Backup failed to start');
    }
    setRunning(false);
  };

  const handleRestore = async (name) => {
    setRestoring(name);
    try {
      await api.post('/backups/restore', {
        snapshot_name: name,
        confirm_token: 'RESTORE-CONFIRM',
      });
      flash('success', `Restored from ${name}. Reloading...`);
      setConfirmRestore(null);
      setTimeout(() => window.location.reload(), 2000);
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Restore failed');
    }
    setRestoring(null);
  };

  const handleDelete = async (name) => {
    try {
      await api.delete(`/backups/snapshots/${encodeURIComponent(name)}`);
      flash('success', `Deleted ${name}`);
      setConfirmDelete(null);
      fetchAll();
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDownload = async (name) => {
    try {
      const r = await api.get(`/backups/snapshots/${encodeURIComponent(name)}/download-url`);
      window.open(r.data.url, '_blank');
    } catch (e) {
      flash('error', 'Could not generate download URL');
    }
  };

  const handleUploadRestore = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      await api.post('/backups/restore-upload?confirm_token=RESTORE-CONFIRM', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      flash('success', 'Restore from file succeeded. Reloading...');
      setConfirmUploadRestore(false);
      setUploadFile(null);
      setTimeout(() => window.location.reload(), 2000);
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Restore upload failed');
    }
    setUploading(false);
  };

  if (loading || !cfg) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const statusColor = cfg.last_backup_status === 'success' ? 'text-green-400' : cfg.last_backup_status === 'failed' ? 'text-red-400' : 'text-muted-foreground';

  return (
    <div data-testid="backup-restore-section" className="space-y-6">
      {msg && (
        <div data-testid="backup-feedback" className={`rounded-xl px-4 py-3 text-sm font-medium flex items-start gap-3 ${
          msg.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
          msg.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
          'bg-blue-500/10 text-blue-400 border border-blue-500/20'}`}>
          {msg.type === 'success' ? <CheckCircle size={18} weight="fill" /> : <Warning size={18} weight="fill" />}
          <span>{msg.m}</span>
        </div>
      )}

      {/* Status card */}
      <div className="glass-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-lg font-bold flex items-center gap-2">
              <Database size={20} className="text-[#FF6B35]" /> Backup & Restore
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Daily encrypted snapshots of your MongoDB + event photos, uploaded to Cloudflare R2 (or any S3-compatible storage).
              Auto-prunes old files. Restore anything in one click.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button onClick={handleRunNow} disabled={!cfg.enabled || running}
              data-testid="run-backup-now-btn"
              title={!cfg.enabled ? 'Enable daily backups and save credentials below first' : ''}
              className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 btn-glow">
              <CloudArrowUp size={18} weight="bold" /> {running ? 'Starting...' : 'Run Backup Now'}
            </button>
            {!cfg.enabled && (
              <p className="text-[10px] text-yellow-400/80 max-w-[200px] text-right" data-testid="backup-disabled-hint">
                Toggle "Enable daily backups" below and click Save first
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-sm">
          <div className="rounded-xl bg-muted/30 px-4 py-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Status</div>
            <div className={`font-semibold mt-1 capitalize ${statusColor}`} data-testid="backup-last-status">
              {cfg.last_backup_status || 'never run'}
            </div>
          </div>
          <div className="rounded-xl bg-muted/30 px-4 py-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Last Backup</div>
            <div className="font-semibold mt-1" data-testid="backup-last-time">{fmtDate(cfg.last_backup_at)}</div>
          </div>
          <div className="rounded-xl bg-muted/30 px-4 py-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Size</div>
            <div className="font-semibold mt-1" data-testid="backup-last-size">{fmtSize(cfg.last_backup_size_bytes)}</div>
          </div>
        </div>

        {cfg.last_error && (
          <div className="mt-4 rounded-xl bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400 break-words" data-testid="backup-last-error">
            <span className="font-semibold">Last error:</span> {cfg.last_error}
          </div>
        )}
      </div>

      {/* Configuration */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-heading text-base font-bold">Cloudflare R2 Configuration</h3>
          <button type="button" onClick={() => updateCfg('enabled', !cfg.enabled)}
            data-testid="backup-enabled-toggle"
            className={`relative inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
              cfg.enabled
                ? 'bg-green-500/15 text-green-400 border border-green-500/40'
                : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/15'
            }`}>
            <span className={`w-2 h-2 rounded-full ${cfg.enabled ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.6)]' : 'bg-yellow-400/70'}`} />
            {cfg.enabled ? 'Daily backups: Enabled' : 'Daily backups: Disabled — tap to enable'}
          </button>
        </div>

        <p className="text-xs text-muted-foreground mb-5">
          Get these from your Cloudflare R2 dashboard → "Manage R2 API Tokens" → "Create API token".
          Endpoint URL is shown on your bucket settings page (looks like <code className="text-[#FF6B35]">https://&lt;account-id&gt;.r2.cloudflarestorage.com</code>).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Access Key ID" value={cfg.access_key_id || ''} onChange={(v) => updateCfg('access_key_id', v)} testId="r2-access-key" />

          <div>
            <label className="overline block mb-2">Secret Access Key</label>
            <div className="relative">
              <input type={showSecret ? 'text' : 'password'}
                value={cfg.secret_access_key || ''}
                onChange={(e) => updateCfg('secret_access_key', e.target.value)}
                placeholder={cfg.secret_access_key_masked || 'Enter secret...'}
                data-testid="r2-secret-key"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 pr-12 transition-all" />
              <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showSecret ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {cfg.secret_access_key_masked && <p className="text-xs text-muted-foreground mt-1">Saved: {cfg.secret_access_key_masked}</p>}
          </div>

          <Field label="Endpoint URL" value={cfg.endpoint_url || ''} onChange={(v) => updateCfg('endpoint_url', v)}
            testId="r2-endpoint" placeholder="https://<account>.r2.cloudflarestorage.com" />
          <Field label="Bucket Name" value={cfg.bucket || ''} onChange={(v) => updateCfg('bucket', v)} testId="r2-bucket" />

          <Field label="Region" value={cfg.region || 'auto'} onChange={(v) => updateCfg('region', v)} testId="r2-region" hint="R2 uses 'auto'" />
          <Field label="Daily Backup Hour (IST)" type="number" value={cfg.schedule_hour_ist ?? 3}
            onChange={(v) => updateCfg('schedule_hour_ist', v)} testId="r2-hour" hint="0-23. Default: 3 AM IST" />

          <Field label="Keep Daily Snapshots" type="number" value={cfg.daily_retention ?? 30}
            onChange={(v) => updateCfg('daily_retention', v)} testId="r2-daily-retention" hint="Auto-prunes older daily snapshots" />
          <Field label="Keep Monthly Snapshots" type="number" value={cfg.monthly_retention ?? 12}
            onChange={(v) => updateCfg('monthly_retention', v)} testId="r2-monthly-retention" hint="One snapshot/month kept beyond daily window" />
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={handleSave} disabled={saving} data-testid="save-backup-config-btn"
            className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:opacity-90">
            <FloppyDisk size={16} /> {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          <button onClick={handleTest} disabled={testing} data-testid="test-r2-btn"
            className="px-5 py-2.5 rounded-full border border-white/15 text-sm font-semibold flex items-center gap-2 hover:bg-white/5 disabled:opacity-50">
            <Plug size={16} /> {testing ? 'Testing...' : 'Test Connection'}
          </button>
          <button onClick={fetchAll} data-testid="refresh-snapshots-btn"
            className="px-5 py-2.5 rounded-full border border-white/15 text-sm font-semibold flex items-center gap-2 hover:bg-white/5">
            <ArrowClockwise size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Snapshots */}
      <div className="glass-card p-6">
        <h3 className="font-heading text-base font-bold mb-1">Snapshots ({snapshots.length})</h3>
        <p className="text-xs text-muted-foreground mb-4">Most recent first. Click Restore to overwrite your live DB with a snapshot.</p>

        {snapshots.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-white/10 rounded-xl" data-testid="no-snapshots">
            No snapshots yet. Run your first backup above.
          </div>
        ) : (
          <div className="space-y-2" data-testid="snapshot-list">
            {snapshots.map(s => (
              <div key={s.key} data-testid={`snapshot-${s.name}`}
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-mono font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(s.last_modified)} • {fmtSize(s.size)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleDownload(s.name)} data-testid={`download-${s.name}`}
                    className="p-2 rounded-lg border border-white/10 text-muted-foreground hover:text-white hover:bg-white/5" title="Download">
                    <DownloadSimple size={16} />
                  </button>
                  <button onClick={() => setConfirmRestore({ name: s.name })}
                    data-testid={`restore-${s.name}`}
                    className="px-3 py-1.5 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 text-xs font-semibold hover:bg-[#00E5FF]/20">
                    Restore
                  </button>
                  <button onClick={() => setConfirmDelete({ name: s.name })}
                    data-testid={`delete-${s.name}`}
                    className="p-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10" title="Delete">
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Disaster recovery: restore from local file */}
      <div className="glass-card p-6 border border-yellow-500/20">
        <h3 className="font-heading text-base font-bold flex items-center gap-2">
          <UploadSimple size={18} className="text-yellow-400" /> Disaster Recovery — Restore from File
        </h3>
        <p className="text-xs text-muted-foreground mt-1 mb-4">
          If R2 is unreachable, you can upload a previously downloaded <code>.tar.gz</code> snapshot here. Max 500 MB.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept=".tar.gz,.tgz,application/gzip"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            data-testid="upload-restore-file"
            className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-white/10 file:text-white hover:file:bg-white/20" />
          {uploadFile && (
            <span className="text-xs text-muted-foreground">{uploadFile.name} ({fmtSize(uploadFile.size)})</span>
          )}
          <button onClick={() => setConfirmUploadRestore(true)} disabled={!uploadFile || uploading}
            data-testid="restore-upload-btn"
            className="px-4 py-2 rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 text-sm font-semibold disabled:opacity-40 hover:bg-yellow-500/30">
            {uploading ? 'Restoring...' : 'Restore from File'}
          </button>
        </div>
      </div>

      {/* Restore confirmation modal */}
      {confirmRestore && (
        <ConfirmModal title="Restore this snapshot?" testId="confirm-restore-modal"
          danger={`This will OVERWRITE your current database with "${confirmRestore.name}". All data added since that snapshot will be lost.`}
          confirmLabel={restoring ? 'Restoring...' : 'Yes, Restore'}
          loading={!!restoring}
          onConfirm={() => handleRestore(confirmRestore.name)}
          onCancel={() => setConfirmRestore(null)} />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <ConfirmModal title="Delete snapshot?" testId="confirm-delete-modal"
          danger={`"${confirmDelete.name}" will be permanently removed from R2. This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => handleDelete(confirmDelete.name)}
          onCancel={() => setConfirmDelete(null)} />
      )}

      {/* Upload restore confirm */}
      {confirmUploadRestore && uploadFile && (
        <ConfirmModal title="Restore from uploaded file?" testId="confirm-upload-restore-modal"
          danger={`This will OVERWRITE your current database with the contents of "${uploadFile.name}". All current data will be lost.`}
          confirmLabel={uploading ? 'Restoring...' : 'Yes, Restore'}
          loading={uploading}
          onConfirm={handleUploadRestore}
          onCancel={() => setConfirmUploadRestore(false)} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testId, hint, placeholder }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 transition-all" />
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function ConfirmModal({ title, danger, confirmLabel, onConfirm, onCancel, loading, testId }) {
  return (
    <div data-testid={testId} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="glass-card max-w-md w-full p-6 border border-red-500/30">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
            <Warning size={20} className="text-red-400" weight="fill" />
          </div>
          <div className="flex-1">
            <h4 className="font-heading text-base font-bold">{title}</h4>
            <p className="text-sm text-muted-foreground mt-2">{danger}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6">
          <button onClick={onCancel} disabled={loading} data-testid="confirm-cancel"
            className="px-4 py-2 rounded-full border border-white/15 text-sm font-medium hover:bg-white/5 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} data-testid="confirm-yes"
            className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
